import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { JSON_SPACE } from 'constants/json'
import { toAgentErrorMessage } from 'features/yieldbirdWrapperAgent/api/errors'
import { getReportFile, getReportStatus } from 'features/yieldbirdWrapperAgent/api/requests'
import { enqueueSnackbarForRestError } from 'providers/tanstack-query-rest/errors'

import type { Report, ReportFileKind } from 'features/yieldbirdWrapperAgent/api/types'

type UseOpenReportFile = {
  open: (jobId: Report['jobId'], kind: ReportFileKind) => Promise<void>
}

const OBJECT_URL_LIFETIME = 60_000 // [ms]

const toJsonBlob = (value: unknown): Blob =>
  new Blob([JSON.stringify(value, null, JSON_SPACE)], { type: 'application/json' })

const prettyPrintJson = async (blob: Blob): Promise<Blob> => {
  try {
    return toJsonBlob(JSON.parse(await blob.text()))
  } catch {
    return blob
  }
}

const fetchFile = async (jobId: Report['jobId'], kind: ReportFileKind): Promise<Blob> => {
  switch (kind) {
    case 'html':
      return new Blob([await getReportFile(jobId, kind)], { type: 'text/html' })
    case 'json':
      return prettyPrintJson(await getReportFile(jobId, kind))
    case 'status':
      return toJsonBlob(await getReportStatus(jobId))
  }
}

const showInTab = (tab: Window, file: Blob): void => {
  const objectUrl: string = URL.createObjectURL(file)

  tab.location.href = objectUrl
  setTimeout((): void => URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME)
}

export const useOpenReportFile = (): UseOpenReportFile => {
  const { t } = useTranslation('features/yieldbirdWrapperAgent')

  const open = async (jobId: Report['jobId'], kind: ReportFileKind): Promise<void> => {
    const tab: null | Window = window.open('', '_blank')

    if (tab === null) {
      enqueueSnackbar(t('reports.popupBlocked'), { variant: 'error' })

      return
    }

    try {
      showInTab(tab, await fetchFile(jobId, kind))
    } catch (error) {
      tab.close()
      enqueueSnackbarForRestError(error, toAgentErrorMessage)
    }
  }

  return { open }
}
